import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onUpload, onClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const newUploads = files?.map((file, index) => ({
      id: Date.now() + index,
      name: file?.name,
      size: file?.size,
      progress: 0,
      status: 'uploading'
    }));

    setUploadProgress(newUploads);

    newUploads?.forEach((upload, index) => {
      simulateUpload(upload?.id, index);
    });
  };

  const simulateUpload = (id, index) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(prev =>
          prev?.map(item =>
            item?.id === id ? { ...item, progress: 100, status: 'completed' } : item
          )
        );
        setTimeout(() => {
          if (index === uploadProgress?.length - 1) {
            onUpload();
          }
        }, 500);
      } else {
        setUploadProgress(prev =>
          prev?.map(item =>
            item?.id === id ? { ...item, progress: Math.round(progress) } : item
          )
        );
      }
    }, 300);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Upload Assets
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label="Close upload"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-all duration-250 ${
              isDragging
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
            }`}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Upload" size={32} className="text-primary" />
              </div>
              <div className="space-y-2">
                <p className="text-base md:text-lg font-medium text-foreground">
                  Drag and drop files here
                </p>
                <p className="text-sm text-muted-foreground">
                  or click to browse from your device
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,video/*,.pdf,.doc,.docx,.svg"
              />
              <Button
                variant="outline"
                iconName="FolderOpen"
                iconPosition="left"
                onClick={() => fileInputRef?.current?.click()}
              >
                Browse Files
              </Button>
              <p className="text-xs text-muted-foreground">
                Supported formats: JPG, PNG, GIF, MP4, PDF, DOC, SVG (Max 50MB)
              </p>
            </div>
          </div>

          {uploadProgress?.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="caption font-semibold text-foreground">
                Uploading {uploadProgress?.length} file(s)
              </h3>
              {uploadProgress?.map((upload) => (
                <div key={upload?.id} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon
                        name={upload?.status === 'completed' ? 'CheckCircle2' : 'Loader2'}
                        size={20}
                        className={upload?.status === 'completed' ? 'text-success' : 'text-primary animate-spin'}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="caption font-medium text-foreground truncate">
                          {upload?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(upload?.size)}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-foreground ml-2">
                      {upload?.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        upload?.status === 'completed' ? 'bg-success' : 'bg-primary'
                      }`}
                      style={{ width: `${upload?.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadZone;